<script setup lang="ts">
/**
 * Отрисовка контентной страницы из блоков.
 *
 * Чисто презентационный компонент: получает страницу и рисует. Им же пользуется
 * превью в админке, поэтому «как в редакторе» и «как у гостя» — это буквально
 * один и тот же результат, а не два похожих.
 *
 * Текст выводим как текст, без v-html: содержимое приходит из params_json, который
 * правится в том числе руками через «Params JSON», и разрешать там разметку значит
 * открыть XSS на витрине ради курсива в абзаце.
 */
import type { ContentPage } from '@shared/config';

defineProps<{ page: ContentPage }>();
</script>

<template>
  <article class="content-page">
    <h1 class="content-page__title brand-font">{{ page.title }}</h1>

    <template v-for="(block, index) in page.blocks" :key="index">
      <h2 v-if="block.type === 'heading' && block.title" class="content-page__heading brand-font">
        {{ block.title }}
      </h2>

      <p v-else-if="block.type === 'text' && block.text" class="content-page__text">{{ block.text }}</p>

      <figure v-else-if="block.type === 'image' && block.imageUrl" class="content-page__figure">
        <img :src="block.imageUrl" :alt="block.title || page.title" loading="lazy" />
        <figcaption v-if="block.title">{{ block.title }}</figcaption>
      </figure>

      <section v-else-if="block.type === 'cards' && block.cards.length" class="content-page__cards-section">
        <h2 v-if="block.title" class="content-page__heading brand-font">{{ block.title }}</h2>
        <div class="content-page__cards">
          <div v-for="(card, cardIndex) in block.cards" :key="cardIndex" class="content-card">
            <img
              v-if="card.imageUrl"
              class="content-card__img"
              :src="card.imageUrl"
              :alt="card.title"
              loading="lazy"
            />
            <div class="content-card__body">
              <div v-if="card.title" class="content-card__title">{{ card.title }}</div>
              <p v-if="card.text" class="content-card__text">{{ card.text }}</p>
            </div>
          </div>
        </div>
      </section>
    </template>
  </article>
</template>

<style scoped lang="scss">
.content-page {
  @apply max-w-3xl mx-auto py-8 md:py-14 flex flex-col gap-6;

  &__title {
    @apply text-3xl md:text-5xl font-bold leading-tight text-gray-900 dark:text-white;
  }

  &__heading {
    @apply text-xl md:text-3xl font-bold leading-tight text-gray-900 dark:text-white mt-4;
  }

  &__text {
    @apply text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300;
    // Переносы строк из редактора сохраняем: админ набирает текст абзацами,
    // а не размечает его тегами
    white-space: pre-line;
  }

  &__figure {
    @apply flex flex-col gap-2;

    img {
      @apply w-full h-auto object-cover rounded-2xl;
    }

    figcaption {
      @apply text-sm text-gray-500 dark:text-gray-400;
    }
  }

  &__cards-section {
    @apply flex flex-col gap-4;
  }

  &__cards {
    @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4;
  }
}

.content-card {
  @apply flex flex-col overflow-hidden rounded-3xl;
  @apply bg-gray-100 dark:bg-zinc-800;

  &__img {
    @apply w-full object-cover;
    aspect-ratio: 1 / 1;
  }

  &__body {
    @apply flex flex-col gap-1 p-4;
  }

  &__title {
    @apply text-base font-bold text-gray-900 dark:text-white;
  }

  &__text {
    @apply text-sm leading-snug text-gray-600 dark:text-gray-300;
    white-space: pre-line;
  }
}
</style>
